/**
 * Stable test ids for the poster editor's DOM chrome (add-item bar, stock panel,
 * bottom bar). Single source of truth shared by the React source (renders
 * `data-testid`) and the Playwright suite (`getByTestId`).
 *
 * NOTE: the poster editor's CANVAS is Fabric.js — individual canvas objects are
 * pixels, not DOM, so they get NO test id. Tests reach them via the
 * `window.posterEditor.whiteboard` bridge, bounding-box + mouse coordinates, and
 * screenshot comparison. These ids are only for the surrounding DOM chrome.
 *
 * Moved here from tests/tests.types.ts (the old `ElementTestId`) so production
 * code no longer imports from the tests tree. Member names + values are kept
 * identical to the originals so no rendered id changes.
 */
export enum PosterEditorTestId {
  STOCK_PANEL_SEARCH_BAR = 'STOCK_PANEL_SEARCH_BAR',
  ADD_ITEM_BAR_MY_UPLOADS = 'ADD_ITEM_BAR_MY_UPLOADS',
  ADD_ITEM_BAR_MEDIA = 'ADD_ITEM_BAR_MEDIA',
  ADD_ITEM_BAR_AI_IMAGES = 'ADD_ITEM_BAR_AI_IMAGES',
  ADD_ITEM_BAR_AI = 'ADD_ITEM_BAR_AI',
  ADD_ITEM_BAR_BACKGROUND = 'ADD_ITEM_BAR_BACKGROUND',
  ADD_ITEM_BAR_LAYOUT = 'ADD_ITEM_BAR_LAYOUT',
  ADD_ITEM_BAR_TEXT = 'ADD_ITEM_BAR_TEXT',
  ADD_ITEM_BAR_RECORD = 'ADD_ITEM_BAR_RECORD',
  STOCK_PANEL_CAROUSEL_SHAPES = 'STOCK_PANEL_CAROUSEL_SHAPES',
  STOCK_PANE_LAZY_GRID = 'STOCK_PANE_LAZY_GRID',
  POSTER_EDITOR_WEB_BOTTOM_BAR_PLAY_PAUSE_BUTTON = 'POSTER_EDITOR_WEB_BOTTOM_BAR_PLAY_PAUSE_BUTTON',
  PAGE_PLAY_PAUSE_BUTTON = 'PAGE_PLAY_PAUSE_BUTTON',
  // Top nav-bar right-side options. BaseNavOption renders `data-testid={id.toUpperCase()}`,
  // so these values are its id prop uppercased (e.g. id="nav-upgrade-option").
  NAV_UPGRADE_OPTION = 'NAV-UPGRADE-OPTION',
  NAV_DOWNLOAD_OPTION = 'NAV-DOWNLOAD-OPTION',
  NAV_PUBLISH_OPTION = 'NAV-PUBLISH-OPTION',
  // Side-panel labeled dropdowns. ContextualMenuDropdownOption renders
  // `data-testid={id.toUpperCase()}` on its dropdown container, so these values
  // are the option's id prop uppercased (e.g. id="item-edge-effects-option").
  EDGE_EFFECTS_DROPDOWN = 'ITEM-EDGE-EFFECTS-OPTION',
  GLOW_DROPDOWN = 'ITEM-GLOW-CONTEXTUAL-OPTION',
  BORDER_DROPDOWN = 'ITEM-BORDER-CONTEXTUAL-OPTION',
  // Side-panel labeled sliders. SliderOptionsContainer renders
  // `data-testid={id.toUpperCase()}` on its root, so these values are the slider's
  // id prop uppercased. Reach the editable input via `.locator('.input-counter-module__input')`.
  OPACITY_SLIDER = 'ITEM-OPACITY-OPTIONS',
  LETTER_SPACING_SLIDER = 'ITEM-LETTER-SPACING',
  OUTLINE_THICKNESS_SLIDER = 'ITEM-OUTLINE-THICKNESS-OPTION',
  BORDER_THICKNESS_SLIDER = 'ITEM-BORDER-THICKNESS-OPTION',
  // === Section 1: Page setup / Background / Layout ===
  // Leveraged dropdowns (PosterEditingBaseDropdownOption) + sliders (ContextualMenuSliderOptions);
  // values are each control's id prop uppercased.
  FOLDS_DROPDOWN = 'PAGE-FOLDS-OPTION',
  BLEED_UNITS_DROPDOWN = 'BLEED-UNIT-DROPDOWN-OPTION',
  BLEED_VALUE_SLIDER = 'BLEED-UNIT-INPUT-OPTION',
  GRID_SIZE_SLIDER = 'GRID-SIZE-SLIDER-OPTION',
  BACKGROUND_STYLE_DROPDOWN = 'DESIGN-BACKGROUND-DROPDOWN',
  // ContextualMenuImageThumbnailOption forwards `data-testid={id.toUpperCase()}` to its ClickableDiv.
  BACKGROUND_IMAGE_THUMBNAIL = 'IMAGE-BACKGROUND-THUMBNAIL-OPTION',
  // Floating object controls (ControlItems renders `data-testid={id.toUpperCase()}` on each <li>).
  DUPLICATE_OBJECT = 'DUPLICATE',
  DELETE_OBJECT = 'DELETE',
  // Add-item-bar lists (ControlledListItemType2 emits data-testid={id.toUpperCase()} on each item).
  ADD_SOLID_BACKGROUND = 'ADD-SOLID-BACKGROUND',
  ADD_GRADIENT_BACKGROUND = 'ADD-GRADIENT-BACKGROUND',
  ADD_MY_BACKGROUNDS = 'ADD-MY-BACKGROUNDS',
  ADD_STOCK_PHOTO_BACKGROUND = 'ADD-STOCK-PHOTO-BACKGROUND',
  ADD_TRANSPARENT_BACKGROUND = 'ADD-TRANSPARENT-BACKGROUND',
  ADD_SCHEDULE_LAYOUT = 'ADD-SCHEDULE-LAYOUT',
  ADD_MENU_LAYOUT = 'ADD-MENU',
  ADD_TABLE_LAYOUT = 'ADD-TABLE-LAYOUT',
  ADD_TEAR_OFF_TABS_LAYOUT = 'ADD-TEAR-OFF-TABS',
  SLIDESHOW_EXPLORE_MEDIA = 'SLIDESHOW-OPTIONS-MEDIA',
  // Add-item modal panel headers (PanelHeader forwards data-testid; panels key it per modal).
  // The page object derives each modal container from its header via `.locator('..')`.
  SOLID_BACKGROUND_HEADER = 'SOLID-COLOR-BACKGROUND-HEADER',
  GRADIENT_BACKGROUND_HEADER = 'GRADIENT-BACKGROUND-HEADER',
  SCHEDULE_PANEL_HEADER = 'ADD-SCHEDULE-HEADER',
  TABLE_PANEL_HEADER = 'ADD-TABLE-HEADER',
  // Background color modal pickers (dropdown-v2 forwards data-testid to its .dropdown-module__container).
  SOLID_BACKGROUND_COLOR_PICKER = 'SOLID-BACKGROUND-COLOR-PICKER',
  GRADIENT_PRIMARY_COLOR_PICKER = 'GRADIENT-PRIMARY-COLOR-PICKER',
  GRADIENT_SECONDARY_COLOR_PICKER = 'GRADIENT-SECONDARY-COLOR-PICKER',
  GRADIENT_TYPE_DROPDOWN = 'GRADIENT-TYPE-DROPDOWN',
  CROP_PANEL_HEADER = 'CROP-PANEL-HEADER',
  // Panel CTAs / headers / labels (PanelFooterPrimaryDefaultAction→Button, PanelHeader, span forward data-testid).
  COLOR_BACKGROUND_APPLY_BUTTON = 'COLOR-BACKGROUND-APPLY-BUTTON',
  MENU_PANEL_HEADER = 'MENU-PANEL-HEADER',
  MENU_NO_ITEMS_LABEL = 'MENU-NO-ITEMS-LABEL',
  FANCY_TEXT_HEADER = 'FANCY-TEXT-HEADER',
  // === Section 3: Effects · Color · Alignment · Editor chrome ===
  // Custom-shadow sliders (Case B — SliderOptionsContainer emits data-testid from the slider id).
  SHADOW_DISTANCE_SLIDER = 'CUSTOM-SHADOW-DISTANCE',
  SHADOW_OPACITY_SLIDER = 'CUSTOM-SHADOW-OPACITY',
  SHADOW_ANGLE_SLIDER = 'CUSTOM-SHADOW-ANGLE',
  SHADOW_BLUR_SLIDER = 'CUSTOM-SHADOW-BLUR',
  // Roundness "Radius" slider (Case B) — the shared curve-edge thickness slider.
  ROUNDNESS_RADIUS_SLIDER = 'ITEM-CURVE-AMOUNT',
  // Shadow style dropdown (Case A — ContextualMenuDropdownOption emits data-testid from id).
  SHADOW_DROPDOWN = 'BASE-ITEM-SHADOW-OPTION',
  // Color picker: the Solid/Linear/Radial type dropdown (Case C — dropdown-v2 forwards data-testid)
  // and the hex / fade inputs (Case C — input-field-v2 forwards inputTestId to its <input>). Only one
  // color picker is mounted at a time, so each value resolves to a single element.
  COLOR_TYPE_DROPDOWN = 'COLOR-TYPE-DROPDOWN',
  COLOR_HEX_INPUT = 'COLOR-HEX-INPUT',
  COLOR_FADE_INPUT = 'COLOR-FADE-INPUT',
  // Alignment-on-design container (Case C — threaded through ContextualMenuControlsOption → Controls
  // onto the <ul>; its #left/#right/… children stay) and the lock-movement info box (Case C — threaded
  // through ContextualMenuInformationBox → InformationBox onto its container <div>).
  ALIGNMENT_ON_DESIGN = 'ALIGNMENT-ON-DESIGN',
  LOCK_MOVEMENT_INFO_BOX = 'LOCK-MOVEMENT-INFO-BOX',
  // --- Section 3 editor chrome (designEditor.page.ts) ---
  // Save + Collaborate nav options (Case A — BaseNavOption emits data-testid={id.toUpperCase()}).
  NAV_COLLABORATE_OPTION = 'NAV-COLLABORATE-OPTION',
  SAVE_BUTTON = 'NAV-SAVE-OPTION',
  // Autosave status pill in auto-save-option (Case C — Pill forwards data-testid to its container <div>);
  // the single status pill emits one of these by save state. The save-label wrapper + general icon are hosts.
  ALL_CHANGES_SAVED_PILL = 'ALL-CHANGES-SAVED-PILL',
  SAVING_PAUSED_PILL = 'SAVING-PAUSED-PILL',
  SAVE_LABEL_CONTAINER = 'SAVE-LABEL-CONTAINER',
  AUTO_SAVE_ICON_GENERAL = 'AUTO-SAVE-ICON-GENERAL',
  // Autosave pill icon by save state (Pill forwards content.iconTestId to its icon-v2 Icon).
  AUTO_SAVE_SAVED_ICON = 'AUTO-SAVE-SAVED-ICON',
  AUTO_SAVE_LOADING_ICON = 'AUTO-SAVE-LOADING-ICON',
  // Spellcheck toggle (Case C — ToggleSwitch forwards data-testid to its <label>).
  SPELLCHECK_TOGGLE = 'SPELLCHECK-TOGGLE',
  // Zoom/magnifier container (Case C — host <div> in poster-nav-opts).
  MAGNIFIER_CONTAINER = 'MAGNIFIER-CONTAINER',
  // File menu (Case C — ClickableDiv + dropdown-v2 forward data-testid; ControlledList item forwards it).
  FILE_MENU_BUTTON = 'FILE-MENU-BUTTON',
  FILE_MENU_PANEL = 'FILE-MENU-PANEL',
  FILE_MENU_SETTINGS_OPTION = 'FILE-MENU-SETTINGS-OPTION',
  // Publish dropdown + its option rows (Case C — dropdown-v2 forwards data-testid).
  PUBLISH_LIST = 'PUBLISH-LIST',
  PUBLISH_LIST_OPTION = 'PUBLISH-LIST-OPTION',
  // Download panel (Case C — panel host <div> + QualityItem forwards data-testid + Button forwards data-testid).
  DOWNLOAD_PANEL = 'DOWNLOAD-PANEL',
  DOWNLOAD_QUALITY_BASIC = 'DOWNLOAD-QUALITY-BASIC',
  DOWNLOAD_QUALITY_PAID = 'DOWNLOAD-QUALITY-PAID',
  DOWNLOAD_BUTTON = 'DOWNLOAD-BUTTON',
  // Publish-as-webpage panel (Case C — dropdown-v2 / input-field-v2 / Button forward data-testid).
  PUBLISH_AS_WEBPAGE_DROPDOWN = 'PUBLISH-AS-WEBPAGE-DROPDOWN',
  WEBPAGE_LINK_INPUT = 'WEBPAGE-LINK-INPUT',
  COPY_WEBPAGE_LINK_BUTTON = 'COPY-WEBPAGE-LINK-BUTTON',
  PUBLISH_WEBPAGE_BUTTON = 'PUBLISH-WEBPAGE-BUTTON',
  UNPUBLISH_WEBPAGE_BUTTON = 'UNPUBLISH-WEBPAGE-BUTTON',
  // === Section 2: Text · Fancy Text · Menu · AI · Uploads/Media ===
  // Leveraged font button (ContextualMenuFontButton emits data-testid from its id) + sliders
  // (ExponentialSliderOptions / ContextualMenuSliderOptions → SliderOptionsContainer emit id.toUpperCase()).
  FONT_BUTTON = 'ITEM-FONT-OPTION',
  FONT_SIZE_SLIDER = 'ITEM-FONT-SIZE-OPTION',
  LINE_HEIGHT_SLIDER = 'TEXT-LINE-HEIGHT-OPTION',
  TEXT_BACKGROUND_OPACITY_SLIDER = 'TEXT-BACKGROUND-COLOR-OPACITY-OPTION',
  TEXT_BACKGROUND_ROUNDNESS_SLIDER = 'TEXT-BACKGROUND-ROUNDNESS-OPTION',
  // Text style + vertical-alignment icon buttons (Case C — StylingOptions forwards data-testid to icon-v2).
  TEXT_BOLD_BUTTON = 'TEXT_BOLD_BUTTON',
  TEXT_ITALIC_BUTTON = 'TEXT_ITALIC_BUTTON',
  TEXT_UNDERLINE_BUTTON = 'TEXT_UNDERLINE_BUTTON',
  TEXT_STRIKE_THROUGH_BUTTON = 'TEXT_STRIKE_THROUGH_BUTTON',
  VERTICAL_TOP_ALIGNMENT_BUTTON = 'VERTICAL_TOP_ALIGNMENT_BUTTON',
  VERTICAL_CENTER_ALIGNMENT_BUTTON = 'VERTICAL_CENTER_ALIGNMENT_BUTTON',
  VERTICAL_BOTTOM_ALIGNMENT_BUTTON = 'VERTICAL_BOTTOM_ALIGNMENT_BUTTON',
  // Font search input (Case C — input-field-v2 forwards inputTestId to its <input>).
  FONT_SEARCH_INPUT = 'FONT_SEARCH_INPUT',
  // Fancy Text modal. Panel forwards data-testid to its root; the 3 colour dropdowns + the
  // Add button are direct data-testids. Shape dropdown leverages its ContextualMenuDropdownOption id.
  FANCY_TEXT_PANEL = 'FANCY_TEXT_PANEL',
  FANCY_TEXT_COLOR_1 = 'FANCY_TEXT_COLOR_1',
  FANCY_TEXT_COLOR_2 = 'FANCY_TEXT_COLOR_2',
  FANCY_TEXT_COLOR_3 = 'FANCY_TEXT_COLOR_3',
  FANCY_TEXT_SHAPE_DROPDOWN = 'FANCY-TEXT-MODAL-SHAPE-OPTION',
  ADD_FANCY_TEXT_BUTTON = 'ADD_FANCY_TEXT_BUTTON',
  // Menu modal. Panel forwards data-testid to its root; CTAs are direct data-testids on Button;
  // select-all is on the Checkbox root (assert via aria-checked, not the inner icon's _invisible).
  MENU_PANEL = 'MENU_PANEL',
  MENU_ADD_BUTTON = 'MENU_ADD_BUTTON',
  MENU_ADD_ITEM_BUTTON = 'MENU_ADD_ITEM_BUTTON',
  MENU_ITEM_SUBMIT_BUTTON = 'MENU_ITEM_SUBMIT_BUTTON',
  MENU_SELECT_ALL_CHECKBOX = 'MENU_SELECT_ALL_CHECKBOX',
  // Menu item basic-info inputs (legacy InputField spreads data-testid onto its <input>).
  MENU_ITEM_NAME_INPUT = 'MENU_ITEM_NAME_INPUT',
  MENU_ITEM_PRICE_INPUT = 'MENU_ITEM_PRICE_INPUT',
  MENU_ITEM_DESCRIPTION_INPUT = 'MENU_ITEM_DESCRIPTION_INPUT',
  // AI panels. AiPromptBox forwards data-testid/inputTestId/submitTestId to its root/textarea/send button;
  // Panel forwards data-testid to its root; AiButton forwards data-testid to its Button. The image &
  // voice prompt boxes are never mounted at the same time, so a value per logical control is unambiguous.
  AI_IMAGE_PROMPT_BOX = 'AI_IMAGE_PROMPT_BOX',
  AI_IMAGE_PROMPT_INPUT = 'AI_IMAGE_PROMPT_INPUT',
  AI_IMAGE_PROMPT_SUBMIT = 'AI_IMAGE_PROMPT_SUBMIT',
  // Stock-panel AI entry (StockAiButton). TOOLS_BUTTON = the "Create with AI" selector shown when the
  // panel offers several AI tools (opens a dropdown); IMAGES_ENTRY = the AI-images entry itself —
  // carried by both the single "Create AI Images" CTA and the "AI Images" dropdown row, so a test
  // reaches it regardless of the panel's render mode.
  STOCK_AI_TOOLS_BUTTON = 'STOCK-AI-TOOLS-BUTTON',
  STOCK_AI_IMAGES_ENTRY = 'STOCK-AI-IMAGES-ENTRY',
  AI_VOICE_PROMPT_INPUT = 'AI_VOICE_PROMPT_INPUT',
  AI_VOICE_PANEL = 'AI_VOICE_PANEL',
  AI_VOICE_WRITE_BUTTON = 'AI_VOICE_WRITE_BUTTON',
  // AI Writer prompt box (shared ai-text-prompt-box, used by voice writer / captions / email
  // campaigns). The prompt textarea (AiTextPromptBoxTextfield) and the generate/send button
  // (AiButton in the footer); only one writer prompt box is open at a time.
  AI_WRITER_PROMPT_INPUT = 'AI_WRITER_PROMPT_INPUT',
  AI_WRITER_PROMPT_SUBMIT = 'AI_WRITER_PROMPT_SUBMIT',
  AI_SUBTITLES_PANEL = 'AI_SUBTITLES_PANEL',
  AI_SUBTITLES_GENERATE_BUTTON = 'AI_SUBTITLES_GENERATE_BUTTON',
  // AI add-item dropdown rows (ControlledListItemType2 emits data-testid={id.toUpperCase()}).
  AI_ITEM_IMAGES = 'AI-ITEMS-ADD-AI-IMAGE',
  AI_ITEM_SUBTITLES = 'AI-ITEMS-ADD-AI-SUBTITLES',
  AI_ITEM_VOICE = 'AI-ITEMS-ADD-AI-VOICE',
  // App-wide premium upsell dialog. premium-upsell-panel forwards this to its root; it opens via
  // showPremiumOnlyFeatureDialog when isReactPremiumUpsellModalEnabled. Poster-editor AI-gate tests
  // (T0254-T0256) assert it appears for non-premium users. Replaces the legacy `#premium-only-dialog`
  // modal-v2 id that the React upsell modal dropped — keep this so the selector can't silently break again.
  PREMIUM_UPSELL_DIALOG = 'PREMIUM_UPSELL_DIALOG',
  // Uploads / media-search modal. Upload-from-device is on a ClickableDiv; Add Selected is the stock
  // panel footer Button. The media search input reuses the existing STOCK_PANEL_SEARCH_BAR container.
  UPLOAD_FROM_DEVICE_BUTTON = 'UPLOAD_FROM_DEVICE_BUTTON',
  CREATE_WITH_AI_BUTTON = 'CREATE_WITH_AI_BUTTON',
  // Stock/media modal tabs (TabItem renders data-testid={tabItem.tabKey}; values are the StockPanelTabs keys).
  SHAPES_TAB = 'SHAPES',
  STICKERS_TAB = 'STICKERS',
  // Search-bar submit control (input-field-v2 control-item renders data-testid={control.id}; value = InputFieldIconID.SEARCH).
  SEARCH_CONTROL_BUTTON = 'SEARCH',
  // Sticker Still/Animated sub-filter pill (StockFilterPillButton renders data-testid={subfilter.testId}).
  STICKER_STILL_FILTER = 'STICKER-STILL-FILTER',
  ADD_SELECTED_BUTTON = 'ADD_SELECTED_BUTTON',
  // The in-editor color picker panel (ColorPicker / color-picker-v2 root). Only one is open at a time,
  // so this resolves to whichever color picker (text / text-background / background) is currently shown;
  // the hex input reuses Section 3's COLOR_HEX_INPUT.
  COLOR_PICKER_PANEL = 'COLOR-PICKER-PANEL',
}
